import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const ComingSoonSettings = ({ settings, onUpdateSettings }) => {
  const [formData, setFormData] = useState({
    is_enabled: false,
    bypass_password: '',
    title: 'Coming Soon',
    description: 'We are working hard to bring you something amazing. Stay tuned!',
    background_image: ''
  });
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Update form data when settings change
  useEffect(() => {
    if (settings) {
      setFormData({
        is_enabled: settings?.is_enabled || false,
        bypass_password: settings?.bypass_password || '',
        title: settings?.title || 'Coming Soon',
        description: settings?.description || 'We are working hard to bring you something amazing. Stay tuned!',
        background_image: settings?.background_image || ''
      });
    }
  }, [settings]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    
    try {
      let result = await onUpdateSettings(formData);
      if (result?.success) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError(result?.error || 'Failed to update settings');
      }
    } catch (err) {
      setError('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let result = '';
    for (let i = 0; i < 12; i++) {
      result += chars?.charAt(Math.floor(Math.random() * chars?.length));
    }
    handleInputChange('bypass_password', result);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-foreground">Coming Soon Settings</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Control site-wide access and coming soon page configuration
        </p>
      </div>

      {/* Status Banner */}
      <div className={`p-4 rounded-lg border-l-4 mb-6 ${
        formData?.is_enabled 
          ? 'bg-amber-500/10 border-amber-500 dark:bg-amber-500/5' :'bg-green-500/10 border-green-500 dark:bg-green-500/5'
      }`}>
        <div className="flex items-center space-x-3">
          <Icon 
            name={formData?.is_enabled ? "AlertTriangle" : "CheckCircle"} 
            size={20} 
            className={formData?.is_enabled ? "text-amber-500" : "text-green-500"}
          />
          <div>
            <p className={`font-medium ${
              formData?.is_enabled 
                ? "text-amber-700 dark:text-amber-300" :"text-green-700 dark:text-green-300"
            }`}>
              {formData?.is_enabled ? 'Coming Soon Mode Active' : 'Site Accessible'}
            </p>
            <p className="text-sm text-muted-foreground">
              {formData?.is_enabled 
                ? 'Visitors see the coming soon page unless they enter the bypass password' :'All visitors can access the site normally'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="CheckCircle" size={16} className="text-green-500" />
            <p className="text-green-600 dark:text-green-400 text-sm">Settings updated successfully!</p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-destructive" />
            <p className="text-destructive text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Settings Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Enable/Disable Toggle */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground">
            Coming Soon Mode
          </label>
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => handleInputChange('is_enabled', !formData?.is_enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData?.is_enabled ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-700'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData?.is_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm font-medium text-foreground">
                {formData?.is_enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              When enabled, non-admin users will see the coming soon page
            </p>
          </div>
        </div>

        {/* Page Configuration */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Title */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Page Title
            </label>
            <input
              type="text"
              value={formData?.title}
              onChange={(e) => handleInputChange('title', e?.target?.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Coming Soon"
            />
          </div>

          {/* Background Image */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Background Image URL (Optional)
            </label>
            <input
              type="url"
              value={formData?.background_image}
              onChange={(e) => handleInputChange('background_image', e?.target?.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="https://example.com/image.jpg"
            />
          </div>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Description
          </label>
          <textarea
            value={formData?.description}
            onChange={(e) => handleInputChange('description', e?.target?.value)}
            rows={4}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            placeholder="Describe what visitors can expect..."
          />
        </div>

        {/* Bypass Password */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Bypass Password
          </label>
          <div className="flex space-x-2">
            <div className="flex-1 relative">
              <input
                type={showPassword ? "text" : "password"}
                value={formData?.bypass_password}
                onChange={(e) => handleInputChange('bypass_password', e?.target?.value)}
                className="w-full px-3 py-2 pr-10 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Enter bypass password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <Icon name={showPassword ? "EyeOff" : "Eye"} size={16} />
              </button>
            </div>
            <button
              type="button"
              onClick={generatePassword}
              className="flex items-center space-x-2 px-3 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-muted/80 transition-colors"
              title="Generate secure password"
            >
              <Icon name="RefreshCw" size={16} />
              <span className="hidden sm:inline">Generate</span>
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            Users can enter this password to bypass the coming soon page. Keep it secure!
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-6 border-t border-border">
          <a
            href="/coming-soon"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="ExternalLink" size={16} />
            <span>Preview Page</span>
          </a>

          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={() => {
                if (settings) {
                  setFormData({
                    is_enabled: settings?.is_enabled || false,
                    bypass_password: settings?.bypass_password || '',
                    title: settings?.title || 'Coming Soon',
                    description: settings?.description || 'We are working hard to bring you something amazing. Stay tuned!',
                    background_image: settings?.background_image || ''
                  });
                }
                setError(null);
              }}
              disabled={saving}
              className="px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              Reset
            </button>
            
            <button
              type="submit"
              disabled={saving}
              className="flex items-center space-x-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-primary-foreground/20 border-t-primary-foreground rounded-full animate-spin"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Icon name="Save" size={16} />
                  <span>Save Settings</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ComingSoonSettings;